import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import Alert from "@mui/material/Alert";

function ForcePasswordChangeModal(props) {
  const { open, loading, onSubmit } = props;
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }
    if (password.toLowerCase() === "temppass") {
      return "Please choose a different password";
    }
    return null;
  };

  const handleSubmit = () => {
    setError("");

    // Validate password
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    onSubmit(newPassword);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Dialog
      disablePortal
      open={open}
      fullWidth={true}
      maxWidth={"sm"}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevent closing by clicking outside or pressing escape
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
    >
      <DialogContent sx={{ py: 4, px: 4 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <LockResetIcon sx={{ fontSize: 56, color: "warning.main", mb: 2 }} />
          <Typography
            sx={{
              color: "black",
              fontSize: "24px",
              fontWeight: 600,
            }}
          >
            Password Change Required
          </Typography>
          <Typography
            sx={{
              margin: "10px 0px",
              fontSize: "14px",
              color: "text.secondary",
            }}
          >
            For security reasons, you must change your password before
            continuing. Please choose a strong password with at least 8
            characters and one number.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ mb: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Typography
          sx={{ fontSize: "12px", color: "text.secondary", mb: 2 }}
        >
          Password requirements:
          <ul style={{ margin: "5px 0 0 0", paddingLeft: "20px" }}>
            <li>At least 8 characters</li>
            <li>At least one number</li>
          </ul>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            size="large"
            loading={loading}
            loadingPosition="start"
            startIcon={<LockResetIcon />}
            sx={{ minWidth: 200 }}
          >
            Change Password
          </LoadingButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default ForcePasswordChangeModal;
